import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ModalComponent } from 'src/app/components/modal/modal.component';
import { FolderHierarchy } from 'src/app/models/folder-hierarchy.model';
import { MyStorageService } from 'src/app/pages/storage/storage.service';
import { CommonService } from 'src/app/services/common.service';
import { ModalService } from 'src/app/services/modal.service';
import { NotificationService } from 'src/app/services/notification.service';
@Component({
  selector: 'app-create-folder-modal',
  standalone: true,
  providers: [HttpClient],
  templateUrl: './create-folder-modal.component.html',
  styleUrls: ['./create-folder-modal.component.scss'],
  imports: [CommonModule, ModalComponent, FormsModule, ReactiveFormsModule, TranslateModule],
})
export class CreateFolderModalComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() fromFolderTree: boolean | null = false;
  @Output() newFolderCreated = new EventEmitter<boolean>();
  @ViewChild('cancelButton') cancelButton!: ElementRef;
  @ViewChild('inputRef') inputRef!: ElementRef;

  subscription?: Subscription;
  parentIdSubscription?: Subscription;
  focusSubscription: Subscription = new Subscription();

  //string
  modalId = 'createFolderModal';
  modalTitle = this.commonService.getTranslatedMessage('CREATE_FOLDER');

  //object
  createFolderForm!: FormGroup;
  selectedParent!: FolderHierarchy;

  //array
  urlId?: string;

  constructor(
    private activatedRoute: ActivatedRoute,
    private commonService: CommonService,
    private formBuilder: FormBuilder,
    private modalService: ModalService,
    private myStorageService: MyStorageService,
    private notificationService: NotificationService,
    private router: Router,
  ) {
    this.createForm();
    this.parentIdSubscription = commonService.objectId$.subscribe((id) => {
      if (id) {
        this.f.parentId.setValue(id.length > 0 ? id : null);
      }
    });
  }

  ngOnInit(): void {
    this.subscribeObjects();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.inputRef.nativeElement.focus();
    }, 500);
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.parentIdSubscription) {
      this.parentIdSubscription.unsubscribe();
    }
    if (this.focusSubscription) {
      this.focusSubscription.unsubscribe();
    }
  }

  onCloseModal(): void {
    this.createFolderForm.reset();
    this.modalService.setCreateFolderModalVisibility(false);
    if (this.fromFolderTree) {
      this.commonService.setFolderTreeModalOpened(true);
      setTimeout(() => {
        this.commonService.showModal('#folderTreeModal');
        this.commonService.setIsEncrypted(false);
      }, 0);
    }
  }

  onSubmit(): void {
    if (this.router.url === '/depom') {
      this.f.encryptedId.setValue(null);
      this.f.parentId.setValue(null);
      this.f.encrypted.setValue(false);
    }
    if (this.createFolderForm.value?.name.trim() === null || this.createFolderForm.value.name.trim() === '' || this.createFolderForm.value?.name.trim() === undefined) {
      const notification = {
        message: this.commonService.getTranslatedMessage('FOLDER_NAME_CANNOT_BE_EMPTY'),
      };
      this.notificationService.warningNotification(notification);

      return;
    }
    if (!this.commonService.restrictSpecialCharacterFromGroupName(this.createFolderForm.value?.name, true)) {
      this.subscription = this.myStorageService.createFolder(this.createFolderForm.value).subscribe({
        next: () => {
          this.cancelButton.nativeElement.click();
          this.commonService.quotaChange$.next(true);
          if (this.f.parentId === null) {
            this.newFolderCreated.emit(true);
            this.commonService.selectedFolderHierarchy$.next({} as FolderHierarchy);
          } else {
            if (!this.fromFolderTree) {
              this.commonService.rebindListener.next();
            }
            const notification = {
              message: this.commonService.getTranslatedMessage('FOLDER_CREATION_SUCCESSFUL'),
            };
            this.notificationService.successNotification(notification);
          }
        },
        error: () => {
          this.cancelButton.nativeElement.click();
        },
        complete: () => {
          if (this.parentIdSubscription) {
            this.parentIdSubscription.unsubscribe();
          }
          this.commonService.selectedFolderHierarchy$.next({} as FolderHierarchy);
        },
      });
    } else {
      const notification = {
        message: this.commonService.getTranslatedMessage('ENTERED_INVALID_CHARACTER'),
      };
      this.notificationService.warningNotification(notification);
    }
  }

  asyncValidator(control: any): Promise<any> {
    const { value } = control;

    return new Promise((resolve) => {
      setTimeout(() => {
        if (typeof value === 'string' && value.trim() === '') {
          resolve({ required: true });
        } else {
          resolve(null);
        }
      }, 500);
    });
  }

  createForm(): void {
    this.createFolderForm = this.formBuilder.group({
      parentId: [null],
      name: [null, [Validators.required, Validators.maxLength(50)], [this.asyncValidator]],
      encrypted: [false],
      encryptedId: [null],
    });
  }

  subscribeObjects(): void {
    this.subscription = this.commonService.selectedFolderHierarchy$.subscribe((item) => {
      if (Object.keys(item).length > 0) {
        this.selectedParent = item;
        this.f.parentId.setValue(this.selectedParent?.parent?.objectId);
        this.f.encrypted.setValue(this.selectedParent?.parent?.encrypted);
        this.f.encryptedId.setValue(this.selectedParent?.parent?.encryptedId);
      } else {
        if (this.fromFolderTree) this.f.parentId.setValue(null);
      }
    });
  }

  get f(): { [key: string]: AbstractControl } {
    return this.createFolderForm.controls;
  }
}
